import React, { useState, useEffect, useRef } from 'react';
// @ts-ignore - react-simple-maps doesn't have official types for React 19
import { ComposableMap, Geographies, Geography, Marker, ZoomableGroup } from 'react-simple-maps';
import { motion, AnimatePresence } from 'framer-motion';
import './TravelMap.css';

// TopoJSON world map data
const geoUrl = 'https://cdn.jsdelivr.net/npm/world-atlas@2/countries-110m.json';

export interface Location {
  id: string;
  name: string;
  coordinates: [number, number]; // [longitude, latitude]
  type: 'lived' | 'visited' | 'want-to-go';
  photos: string[];
  stamp?: string; // Path to postal stamp image
  date?: string;
  description?: string;
}

// Location data - you can customize this
const locations: Location[] = [
  // Places I've Lived
  {
    id: 'goa',
    name: 'Goa, India',
    coordinates: [73.5, 15.5],
    type: 'lived',
    photos: [
      '/content/locations/Goa/IMG_20191217_183325.jpg',
      '/content/locations/Goa/IMG_20200209_185119.jpg',
      '/content/locations/Goa/IMG_4889.JPG'
    ],
    stamp: '/content/stamps/goa.jpeg',
    date: 'Born & Raised',
    description: 'Endless coastline, sossegado living. Where it all started 🌊'
  },
  {
    id: 'indiana',
    name: 'Indiana, USA',
    coordinates: [-86.5264, 39.1653],
    type: 'lived',
    photos: [
      '/content/locations/Indiana/578CC2C0-DCCD-41F1-850B-3E6A68A6C5A8_1_105_c.jpeg',
      '/content/locations/Indiana/DSCF4781.JPG',
      '/content/locations/Indiana/IMG_1218.JPG'
    ],
    stamp: '/content/stamps/Indiana.png',
    date: '2024 - Present',
    description: 'Somewhere in the Midwest, designing things at IU'
  },

  // Places I've Visited - USA
  {
    id: 'new-york',
    name: 'New York, USA',
    coordinates: [-74.0060, 40.7128],
    type: 'visited',
    photos: [
      '/content/locations/New York/89E11DF8-F600-4633-A9B2-C5A5A18CA93E_1_105_c.jpeg',
      '/content/locations/New York/9FE8F028-80C8-45E6-BC09-3542EAB1787F_1_105_c.jpeg',
      '/content/locations/New York/F3191E65-2F63-4D65-9BC5-7CC65E460E19_1_105_c.jpeg'
    ],
    stamp: '/content/stamps/New York.png',
    description: 'Yellow cabs and skyscrapers. Pretty wild'
  },
  {
    id: 'chicago',
    name: 'Chicago, USA',
    coordinates: [-87.6298, 41.8781],
    type: 'visited',
    photos: [
      '/content/locations/Chicago/IMG_0262.JPG',
      '/content/locations/Chicago/IMG_0387.JPG',
      '/content/locations/Chicago/IMG_0388.JPG'
    ],
    stamp: '/content/stamps/Chicago.png',
    description: 'Deep dish debates, lakefront views. Good times'
  },
  {
    id: 'boston',
    name: 'Boston, USA',
    coordinates: [-71.0589, 42.3601],
    type: 'visited',
    photos: [
      '/content/locations/Boston/466F2A96-2707-4DF9-BCA4-F92675B60EAC_1_105_c.jpeg',
      '/content/locations/Boston/D6AAC06C-A12C-4988-8D26-9DBCED23B6D5_1_105_c.jpeg',
      '/content/locations/Boston/DFFF1A65-BF5A-4A0E-9A9C-517902CC4585_1_105_c.jpeg'
    ],
    stamp: '/content/stamps/boston.png',
    description: 'Old cobblestones, new ideas. Vibes checked out'
  },
  {
    id: 'miami',
    name: 'Miami, USA',
    coordinates: [-80.1918, 25.7617],
    type: 'visited',
    photos: [
      '/content/locations/Miami/IMG_0784.JPG',
      '/content/locations/Miami/IMG_0843.JPG',
      '/content/locations/Miami/IMG_0907.JPG'
    ],
    stamp: '/content/stamps/Miami.png',
    description: 'Art Deco everything, perpetual golden hour'
  },
  {
    id: 'atlanta',
    name: 'Atlanta, USA',
    coordinates: [-84.3880, 33.7490],
    type: 'visited',
    photos: [
      '/content/locations/Atlanta/IMG_0963.JPG',
      '/content/locations/Atlanta/IMG_0964.JPG'
    ],
    stamp: '/content/stamps/Atlanta.png',
    description: 'Southern heat, trap beats, culture runs deep'
  },
  {
    id: 'nashville',
    name: 'Nashville, USA',
    coordinates: [-86.7816, 36.1627],
    type: 'visited',
    photos: [
      '/content/locations/Nashville/IMG_0955.jpg'
    ],
    stamp: '/content/stamps/Nashville.png',
    description: 'Neon lights, honky tonks. Broadway hits different'
  },

  // Places I've Visited - India
  {
    id: 'mumbai',
    name: 'Mumbai, India',
    coordinates: [71.8, 19.8],
    type: 'visited',
    photos: [
      '/content/locations/Mumbai/IMG_1411.JPG',
      '/content/locations/Mumbai/IMG_20190604_195122.jpg',
      '/content/locations/Mumbai/LRM_EXPORT_7879963345111_20190615_195230041.jpeg'
    ],
    stamp: '/content/stamps/mumbai.jpg',
    description: 'Maximum city energy. Dreams and deadlines at 2 AM'
  },
  {
    id: 'karwar',
    name: 'Karwar, India',
    coordinates: [75.5, 13.8],
    type: 'visited',
    photos: [
      '/content/locations/Karwar/IMG_20210129_162527.jpg',
      '/content/locations/Karwar/IMG_3655.JPG',
      '/content/locations/Karwar/IMG_5363.JPG'
    ],
    stamp: '/content/stamps/karwar.jpeg',
    description: 'Quiet beaches, Konkan sunsets. Just peace'
  },
  {
    id: 'lonavla',
    name: 'Lonavla, India',
    coordinates: [73.8, 17.8],
    type: 'visited',
    photos: [
      '/content/locations/Lonavla/IMG_5781.JPG',
      '/content/locations/Lonavla/IMG_5804.JPG',
      '/content/locations/Lonavla/IMG_6809.jpg'
    ],
    stamp: '/content/stamps/Lonavla .png',
    description: 'Monsoons in the Ghats. Clouds, chikki, chai'
  },

  // Places I've Visited - International
  {
    id: 'istanbul',
    name: 'Istanbul, Turkey',
    coordinates: [28.9784, 41.0082],
    type: 'visited',
    photos: [
      '/content/locations/Istanbul/625C094C-135B-4F3C-9BF1-D58FF14DE836_1_105_c.jpeg',
      '/content/locations/Istanbul/8C78D2A2-A5E6-442E-A98C-6390B7A9A767_1_105_c.jpeg'
    ],
    stamp: '/content/stamps/Istanbul.png',
    description: 'Two continents, one city. Minarets meet skyscrapers'
  }
];

const TravelMap: React.FC = () => {
  const [hoveredLocation, setHoveredLocation] = useState<Location | null>(null);
  const [scatteredImages, setScatteredImages] = useState<Array<{
    id: string;
    src: string;
    x: number;
    y: number;
    rotation: number;
    locationName: string;
    stamp?: string;
    date?: string;
    description?: string;
  }>>([]);
  const [animateIn, setAnimateIn] = useState(false);
  const mapContainerRef = useRef<HTMLDivElement>(null);

  // Pastel color palette for different continents (darker pastel tones)
  const getRegionColor = (geoName: string) => {
    const continentColors: { [key: string]: string } = {
      // North America - Darker Blue
      'United States of America': '#A8CEE8',
      'Canada': '#A8CEE8',
      'Mexico': '#A8CEE8',
      'Guatemala': '#A8CEE8',
      'Belize': '#A8CEE8',
      'Honduras': '#A8CEE8',
      'El Salvador': '#A8CEE8',
      'Nicaragua': '#A8CEE8',
      'Costa Rica': '#A8CEE8',
      'Panama': '#A8CEE8',
      'Cuba': '#A8CEE8',
      'Jamaica': '#A8CEE8',
      'Haiti': '#A8CEE8',
      'Dominican Republic': '#A8CEE8',

      // South America - Darker Coral
      'Brazil': '#FFBFBF',
      'Argentina': '#FFBFBF',
      'Chile': '#FFBFBF',
      'Colombia': '#FFBFBF',
      'Peru': '#FFBFBF',
      'Venezuela': '#FFBFBF',
      'Ecuador': '#FFBFBF',
      'Bolivia': '#FFBFBF',
      'Paraguay': '#FFBFBF',
      'Uruguay': '#FFBFBF',
      'Guyana': '#FFBFBF',
      'Suriname': '#FFBFBF',

      // Europe - Darker Lavender
      'United Kingdom': '#DCC8F0',
      'France': '#DCC8F0',
      'Germany': '#DCC8F0',
      'Italy': '#DCC8F0',
      'Spain': '#DCC8F0',
      'Portugal': '#DCC8F0',
      'Netherlands': '#DCC8F0',
      'Belgium': '#DCC8F0',
      'Switzerland': '#DCC8F0',
      'Austria': '#DCC8F0',
      'Poland': '#DCC8F0',
      'Czech Republic': '#DCC8F0',
      'Greece': '#DCC8F0',
      'Turkey': '#DCC8F0',
      'Sweden': '#DCC8F0',
      'Norway': '#DCC8F0',
      'Finland': '#DCC8F0',
      'Denmark': '#DCC8F0',
      'Ireland': '#DCC8F0',
      'Russia': '#DCC8F0',
      'Ukraine': '#DCC8F0',
      'Romania': '#DCC8F0',
      'Hungary': '#DCC8F0',
      'Bulgaria': '#DCC8F0',
      'Serbia': '#DCC8F0',
      'Croatia': '#DCC8F0',

      // Asia - Darker Mint
      'China': '#BFE8D5',
      'India': '#BFE8D5',
      'Japan': '#BFE8D5',
      'South Korea': '#BFE8D5',
      'Thailand': '#BFE8D5',
      'Vietnam': '#BFE8D5',
      'Indonesia': '#BFE8D5',
      'Malaysia': '#BFE8D5',
      'Singapore': '#BFE8D5',
      'Philippines': '#BFE8D5',
      'Pakistan': '#BFE8D5',
      'Bangladesh': '#BFE8D5',
      'Myanmar': '#BFE8D5',
      'Cambodia': '#BFE8D5',
      'Laos': '#BFE8D5',
      'Nepal': '#BFE8D5',
      'Sri Lanka': '#BFE8D5',
      'Mongolia': '#BFE8D5',
      'Kazakhstan': '#BFE8D5',

      // Africa - Darker Peach
      'Egypt': '#FFD4B8',
      'South Africa': '#FFD4B8',
      'Nigeria': '#FFD4B8',
      'Kenya': '#FFD4B8',
      'Ethiopia': '#FFD4B8',
      'Morocco': '#FFD4B8',
      'Algeria': '#FFD4B8',
      'Tunisia': '#FFD4B8',
      'Ghana': '#FFD4B8',
      'Tanzania': '#FFD4B8',
      'Uganda': '#FFD4B8',
      'Libya': '#FFD4B8',
      'Sudan': '#FFD4B8',
      'Zimbabwe': '#FFD4B8',
      'Mozambique': '#FFD4B8',

      // Oceania - Darker Yellow
      'Australia': '#FFF9B8',
      'New Zealand': '#FFF9B8',
      'Papua New Guinea': '#FFF9B8',
      'Fiji': '#FFF9B8',
    };

    return continentColors[geoName] || '#D8D8D8'; // Default light gray
  };

  // Animate pins on mount
  useEffect(() => {
    const timer = setTimeout(() => setAnimateIn(true), 300);
    return () => clearTimeout(timer);
  }, []);

  const handleMarkerMouseEnter = (location: Location, event: React.MouseEvent<SVGGElement>) => {
    setHoveredLocation(location);

    if (!mapContainerRef.current || location.photos.length === 0) return;

    const containerRect = mapContainerRef.current.getBoundingClientRect();
    const markerRect = event.currentTarget.getBoundingClientRect();

    // Calculate marker position relative to container
    const markerX = markerRect.left - containerRect.left + markerRect.width / 2;
    const markerY = markerRect.top - containerRect.top + markerRect.height / 2;

    // Calculate map center (offset slightly toward top)
    const centerX = containerRect.width / 2;
    const centerY = containerRect.height / 2 - 60; // Move 60px toward the top

    // Create scattered images around the center of the map with even spacing
    const totalPhotos = location.photos.length;
    const angleSpread = Math.PI * 2; // Full 360 degrees

    const images = location.photos.map((photo, index) => {
      // Calculate evenly distributed angle around the center
      const angle = (index * (angleSpread / totalPhotos)) + (Math.PI / 4); // Start from 45 degrees

      // Vary radius slightly for each image to create depth
      const baseRadius = 120;
      const radiusVariation = 40 * (index % 2 === 0 ? 1 : -1); // Alternate closer/farther
      const radius = baseRadius + radiusVariation;

      // Position around the map center instead of marker
      const x = centerX + Math.cos(angle) * radius;
      const y = centerY + Math.sin(angle) * radius;
      const rotation = (index % 2 === 0 ? 1 : -1) * (5 + Math.random() * 10); // Alternate rotation direction

      return {
        id: `${location.id}-${index}`,
        src: photo,
        x,
        y,
        rotation,
        locationName: location.name,
        stamp: location.stamp,
        date: location.date,
        description: location.description
      };
    });

    setScatteredImages(images);
  };

  const handleMarkerMouseLeave = () => {
    setHoveredLocation(null);
    setScatteredImages([]);
  };

  const getPinColor = (type: Location['type']) => {
    switch (type) {
      case 'lived':
        return '#ef4444'; // Red
      case 'visited':
        return '#3b82f6'; // Blue
      case 'want-to-go':
        return '#a855f7'; // Purple
      default:
        return '#6b7280'; // Gray
    }
  };

  const getPinIcon = (type: Location['type']) => {
    switch (type) {
      case 'lived':
        return '🏠';
      case 'visited':
        return '📍';
      case 'want-to-go':
        return '🎯';
      default:
        return '📍';
    }
  };

  const getStampTiltClass = (locationId: string) => {
    // Assign unique tilt to each location for authentic stamp look
    const tiltMap: { [key: string]: string } = {
      'goa': 'stamp-tilt-1',
      'indiana': 'stamp-tilt-2',
      'new-york': 'stamp-tilt-3',
      'chicago': 'stamp-tilt-4',
      'boston': 'stamp-tilt-5',
      'miami': 'stamp-tilt-6',
      'atlanta': 'stamp-tilt-7',
      'nashville': 'stamp-tilt-8',
      'mumbai': 'stamp-tilt-9',
      'karwar': 'stamp-tilt-10',
      'lonavla': 'stamp-tilt-11',
      'istanbul': 'stamp-tilt-12'
    };
    return tiltMap[locationId] || 'stamp-tilt-1';
  };

  return (
    <div className="bento-item bento-travel-map">
      <div className="widget-content travel-map-widget">
        <div className="travel-map-header">
          <h3 className="travel-map-title">Exploration</h3>
        </div>

        <div className="map-container" ref={mapContainerRef}>
          <ComposableMap
            projection="geoMercator"
            projectionConfig={{
              scale: 154.35,
              center: [0, 20]
            }}
            className="composable-map"
          >
            <ZoomableGroup zoom={1.05} minZoom={1.05} maxZoom={1.05}>
              <Geographies geography={geoUrl}>
                {({ geographies }: { geographies: any[] }) =>
                  geographies.map((geo: any) => {
                    const countryName = geo.properties.name;
                    const fillColor = getRegionColor(countryName);

                    return (
                      <Geography
                        key={geo.rsmKey}
                        geography={geo}
                        className="geography-path"
                        style={{
                          default: {
                            fill: fillColor,
                            stroke: 'rgba(255, 255, 255, 0.5)',
                            strokeWidth: 0.5,
                            outline: 'none',
                          },
                          hover: {
                            fill: fillColor,
                            stroke: 'rgba(255, 255, 255, 0.8)',
                            strokeWidth: 0.75,
                            outline: 'none',
                          },
                          pressed: {
                            fill: fillColor,
                            stroke: 'rgba(255, 255, 255, 0.8)',
                            strokeWidth: 0.75,
                            outline: 'none',
                          },
                        }}
                      />
                    );
                  })
                }
              </Geographies>

              {/* Markers with stagger animation */}
              {locations.map((location, index) => (
                <Marker
                  key={location.id}
                  coordinates={location.coordinates}
                >
                  <motion.g
                    initial={{ scale: 0, opacity: 0, y: -50 }}
                    animate={
                      animateIn
                        ? {
                            scale: 1,
                            opacity: 1,
                            y: 0,
                            transition: {
                              delay: index * 0.15,
                              type: 'spring',
                              stiffness: 260,
                              damping: 20
                            }
                          }
                        : {}
                    }
                    whileHover={{
                      scale: 1.3,
                      transition: { duration: 0.2 }
                    }}
                    onMouseEnter={(e) => handleMarkerMouseEnter(location, e)}
                    onMouseLeave={handleMarkerMouseLeave}
                    className="marker-group"
                    style={{ cursor: 'pointer' }}
                  >
                    {/* Pulsing circle background */}
                    <motion.circle
                      cx={0}
                      cy={0}
                      r={4}
                      fill="#10b981"
                      opacity={0.4}
                      animate={{
                        scale: [1, 2, 1],
                        opacity: [0.4, 0, 0.4]
                      }}
                      transition={{
                        duration: 2,
                        repeat: Infinity,
                        ease: 'easeInOut'
                      }}
                    />

                    {/* Pin marker */}
                    <circle
                      cx={0}
                      cy={0}
                      r={2.5}
                      fill="#10b981"
                      className={`pin-marker pin-${location.type}`}
                    />

                    {/* Location name label */}
                    <text
                      textAnchor="middle"
                      y={16}
                      className="marker-label"
                      style={{
                        fontSize: '7px',
                        fontWeight: 600,
                        fill: 'var(--text-color)',
                        pointerEvents: 'none'
                      }}
                    >
                      {location.name.split(',')[0]}
                    </text>
                  </motion.g>
                </Marker>
              ))}
            </ZoomableGroup>
          </ComposableMap>

          {/* Scattered images overlay */}
          <AnimatePresence>
            {scatteredImages.map((image, index) => (
              <motion.div
                key={image.id}
                className="scattered-image-card"
                initial={{ opacity: 0, scale: 0 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0 }}
                transition={{
                  duration: 0.3,
                  delay: index * 0.1,
                  type: 'spring',
                  stiffness: 300,
                  damping: 25
                }}
                style={{
                  position: 'absolute',
                  left: `${image.x}px`,
                  top: `${image.y}px`,
                  transform: `translate(-50%, -50%) rotate(${image.rotation}deg)`,
                  pointerEvents: 'none',
                  zIndex: 1000
                }}
              >
                <div className="scattered-polaroid">
                  {/* Stamp overlay */}
                  {image.stamp && (
                    <div className={`scattered-stamp ${getStampTiltClass(image.id.split('-')[0])}`}>
                      <img src={image.stamp} alt="Stamp" className="stamp-image-scattered" />
                    </div>
                  )}

                  {/* Photo */}
                  <img
                    src={image.src}
                    alt={image.locationName}
                    className="scattered-photo"
                  />

                  {/* Location info */}
                  <div className="scattered-info">
                    <p className="scattered-location-name">{image.locationName}</p>
                    {image.date && <p className="scattered-date">{image.date}</p>}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      </div>
    </div>
  );
};

export default TravelMap;
